/**
 * Lightweight toast context + provider (Task 13, design spec §8).
 *
 * Follows Sonner principles:
 *  - Spatial consistency: toasts enter/exit from the same edge (bottom-right).
 *  - CSS transitions (not keyframes) so rapid additions retarget smoothly.
 *  - Pause timer when the tab is hidden.
 *  - Slightly slower + ease (more elegant than ease-out for a "notice").
 *
 * Usage:
 *   const { showToast } = useContext(ToastContext);
 *   showToast("Something happened");
 */

import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface Toast {
  id: string;
  message: string;
  /** Duration in ms before auto-dismiss (default 5000) */
  duration?: number;
}

interface ToastContextValue {
  showToast: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue>({
  showToast: () => undefined,
});

export const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [toasts, setToasts] = useState<(Toast & { visible: boolean })[]>([]);
  // Per-toast teardown: clears the auto-dismiss timer AND removes that toast's
  // visibilitychange listener. Keyed by id so we can tear down exactly one toast
  // on dismiss, and all remaining toasts on provider unmount.
  const cleanupMap = useRef<Map<string, () => void>>(new Map());

  // Remove a toast from state after its exit transition
  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Begin exit animation, then remove after transition (~350ms)
  const dismiss = useCallback(
    (id: string) => {
      // Tear down this toast's timer + visibilitychange listener before it goes.
      const cleanup = cleanupMap.current.get(id);
      if (cleanup) {
        cleanup();
        cleanupMap.current.delete(id);
      }
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, visible: false } : t)),
      );
      setTimeout(() => remove(id), 380);
    },
    [remove],
  );

  const showToast = useCallback(
    (message: string, duration = 5000) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setToasts((prev) => [...prev, { id, message, duration, visible: true }]);

      // Auto-dismiss timer — paused when tab is hidden
      let remaining = duration;
      let started = Date.now();
      let timer: ReturnType<typeof setTimeout>;

      const start = () => {
        timer = setTimeout(() => dismiss(id), remaining);
      };

      const pause = () => {
        clearTimeout(timer);
        remaining -= Date.now() - started;
      };

      const resume = () => {
        started = Date.now();
        start();
      };

      const handleVisibility = () => {
        if (document.hidden) {
          pause();
        } else {
          resume();
        }
      };

      const cleanup = () => {
        clearTimeout(timer);
        document.removeEventListener("visibilitychange", handleVisibility);
      };

      document.addEventListener("visibilitychange", handleVisibility);
      start();
      cleanupMap.current.set(id, cleanup);

      return cleanup;
    },
    [dismiss],
  );

  // Tear down every live toast (timers + visibilitychange listeners) on unmount
  useEffect(() => {
    const map = cleanupMap.current;
    return () => {
      map.forEach((cleanup) => cleanup());
      map.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastList toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

// ─── ToastList ────────────────────────────────────────────────────────────────

interface ToastListProps {
  toasts: (Toast & { visible: boolean })[];
  onDismiss: (id: string) => void;
}

const ToastList: React.FC<ToastListProps> = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column-reverse",
        gap: "8px",
        maxWidth: "380px",
        width: "calc(100vw - 48px)",
        pointerEvents: "none",
      }}
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={() => onDismiss(toast.id)}
        />
      ))}
    </div>
  );
};

// ─── ToastItem ────────────────────────────────────────────────────────────────

interface ToastItemProps {
  toast: Toast & { visible: boolean };
  onDismiss: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  return (
    <div
      role="status"
      style={{
        /* CSS transition (not keyframes) for smooth retargeting — spec §8 */
        transition: "opacity 0.35s ease, transform 0.35s ease",
        opacity: toast.visible ? 1 : 0,
        transform: toast.visible ? "translateY(0)" : "translateY(12px)",
        background: "#1a1a1a",
        color: "#fff",
        borderRadius: "8px",
        padding: "12px 16px",
        fontSize: "14px",
        lineHeight: "1.5",
        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        pointerEvents: "all",
        cursor: "default",
      }}
    >
      <span style={{ flex: 1 }}>{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        style={{
          background: "none",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          cursor: "pointer",
          padding: "0",
          fontSize: "16px",
          lineHeight: 1,
          flexShrink: 0,
          marginTop: "1px",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.color = "#fff")
        }
        onMouseLeave={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.color =
            "rgba(255,255,255,0.6)")
        }
      >
        ✕
      </button>
    </div>
  );
};

export default ToastContext;
